/**
 * Business Case financials (UAT §7).
 *
 * In the legacy build these figures were computed inline inside the renderer, which made
 * them untestable — and they are the numbers a hiring decision gets argued from, so they
 * are the last thing that should live only in a view. The arithmetic below is the legacy
 * arithmetic, reproduced exactly; the reasoning behind each choice is kept with it because
 * every one of them corrects a way the comparison would otherwise be biased:
 *
 *  - Hire cost scales with the real gap. Pricing one average hire against a 2 FTE gap
 *    understates what closing it costs and biases the answer toward "hire".
 *  - You hire whole people, so the gap rounds up — and a real, non-zero gap always costs
 *    at least one hire even when it rounds under 1.
 *  - The contractor figure is a 12-week bridge; the hire figure is annual. Comparing them
 *    directly makes the contractor look cheaper regardless of the real economics, so the
 *    hire cost is prorated to the same 12-week window for the comparison. The full annual
 *    figure is still what gets reported.
 *  - Built-sourced budgets take priority over manual salary entries.
 */

const round2 = (n) => Math.round(n * 100) / 100;

/**
 * @param {object} S       hydrated state
 * @param {object} engine  engine bound to that state
 * @param {string} deptId  a department id, or 'ALL'
 */
export function computeBusinessCase(S, engine, deptId = 'ALL') {
  const costs = S.costs ?? {};
  const isAll = deptId === 'ALL';
  const dep = isAll ? null : S.depts.find((d) => d.id === deptId);
  const signal = dep ? engine.computeDeptSignal(dep) : null;

  const scopeRoles = isAll ? S.roles : S.roles.filter((r) => r.dept === deptId);
  const avgSalary = scopeRoles.length
    ? scopeRoles.reduce((s, r) => s + (r.salary || 80000), 0) / scopeRoles.length
    : 80000;
  const avgTCC = scopeRoles.length
    ? scopeRoles.reduce((s, r) => s + (r.salary || 80000) * (1 + (r.stiPct || 0) / 100), 0) / scopeRoles.length
    : avgSalary;

  // Only seats Built has actually approved and funded, counted through the same shared
  // allocation every other screen uses.
  const allocations = engine.getPlanAllocations();
  const eligiblePlans = (S.headcountPlan ?? []).filter((p) => {
    if (!isAll && p.dept !== deptId) return false;
    const alloc = allocations[p.id];
    return engine.isHireEligible(p) && alloc && alloc.vacant > 0;
  });

  const eligibleVacantSeats = eligiblePlans.reduce((s, p) => s + (allocations[p.id]?.vacant ?? 0), 0);
  const builtTotalVacCost = eligiblePlans.reduce(
    (s, p) => s + (p.annualBudget || 0) * (allocations[p.id]?.vacant ?? 0), 0,
  );
  const builtAvgLoaded = eligibleVacantSeats > 0 ? builtTotalVacCost / eligibleVacantSeats : null;

  const usingBuiltCost = builtAvgLoaded != null;
  const loadedMult = costs.loadedMult || 1.3;
  const loadedCost = usingBuiltCost ? builtAvgLoaded : round2(avgTCC * loadedMult);
  const recruitCost = round2((usingBuiltCost ? builtAvgLoaded / loadedMult : avgTCC) * 0.2);

  // ── The seat gap, in each signal type's own terms ──────────────────────────
  const orgHC = S.settings.orgHeadcount || 335;
  let gapFTE = 0;
  let gapBasis = '';

  if (signal?.kind === 'volume') {
    gapFTE = Math.max(0, signal.volGap);
    gapBasis = `${gapFTE.toFixed(2)} FTE implied by the volume threshold (productive-FTE weighted)`;
  } else if (signal?.kind === 'support') {
    // Compared against the same headcount-exposed subset that produced the ratio. Using the
    // full department roster — which also holds requisition, volume and complexity roles —
    // understates or hides a real gap.
    const impliedSeats = signal.benchmark > 0 ? Math.ceil(orgHC / signal.benchmark) : 0;
    const hcStaff = signal.hcStaff || 0;
    gapFTE = round2(Math.max(0, impliedSeats - hcStaff));
    gapBasis = `${gapFTE} seat(s) implied by the 1:${signal.benchmark} staffing benchmark (${hcStaff} headcount-exposed staff vs ${impliedSeats} implied)`;
  } else if (signal?.kind === 'coverage') {
    gapBasis = "Coverage departments don't reduce to a seat-gap number — see the Coverage Map for where the actual risk sits.";
  } else if (signal?.kind === 'judgment') {
    gapBasis = 'Judgment departments have no formula-driven seat gap — this is a manager call, not a model output.';
  } else if (!signal) {
    gapFTE = S.depts.reduce((sum, d) => {
      const s = engine.computeDeptSignal(d);
      return sum + (s.kind === 'volume' && s.flagged && s.volGap > 0 ? s.volGap : 0);
    }, 0);
    gapBasis = `${round2(gapFTE)} FTE implied across flagged volume-threshold departments, org-wide`;
  }

  const hiresNeeded = gapFTE > 0 ? Math.max(1, Math.ceil(gapFTE)) : 0;
  const totalHireCost = (loadedCost + recruitCost) * Math.max(hiresNeeded, 1);

  // Not every needed seat necessarily exists in Built yet — the shortfall still needs a
  // Position Request, and saying so is the difference between a real plan and a wish.
  const partialPositionCoverage =
    hiresNeeded > 0 && eligibleVacantSeats > 0 && eligibleVacantSeats < hiresNeeded;

  const roleHrs = scopeRoles[0]?.hrs != null ? scopeRoles[0].hrs : 40;
  const ctrCost = round2(gapFTE * roleHrs * ((costs.ctrRate || 800) / 8) * 12);
  const hireCostFor12Wks = round2(totalHireCost * 12 / 52);

  const revenueAtRisk = isAll
    ? S.depts.reduce((sum, d) => {
      const s = engine.computeDeptSignal(d);
      return sum + engine.computeRevenueAtRisk(d.id, s.flagged, s.watch);
    }, 0)
    : engine.computeRevenueAtRisk(deptId, !!signal?.flagged, !!signal?.watch);

  const netCase = revenueAtRisk - totalHireCost;
  const financiallyFavorable = revenueAtRisk > totalHireCost;

  const { verdict, verdictBasis } = engine.computeBizCaseVerdict(signal, {
    hasEligiblePosition: eligibleVacantSeats > 0,
    financiallyFavorable,
    hireCostFor12Wks,
    ctrCost,
    fmt$: (n) => '$' + Math.round(n).toLocaleString(),
  });

  // Governance is tracked independently of status: a department can look fine today and
  // still be running on a methodology nobody has approved.
  const governance = [];
  if (dep?.volDrivers?.length > 1 && !dep.volCombineLocked) {
    governance.push(
      'Methodology Pending Approval. This department has more than one volume driver and is currently running under the default MAX combination — nobody has reviewed or locked that choice yet. If SUM turns out to be the right method, this seat gap and verdict could change substantially.',
    );
  }
  if (dep && (dep.volPeriodBasis ?? 'current') !== 'current' && !dep.volPeriodBasisLocked) {
    governance.push(
      'Period Basis Pending Approval. This department is calibrated to a time-period basis that has not been locked by a named approver.',
    );
  }

  return {
    dep, signal,
    gapFTE: round2(gapFTE), gapBasis,
    hiresNeeded, eligibleVacantSeats, partialPositionCoverage,
    loadedCost, recruitCost, usingBuiltCost, avgSalary, avgTCC,
    totalHireCost: round2(totalHireCost),
    hireCostFor12Wks, ctrCost,
    revenueAtRisk, netCase: round2(netCase), financiallyFavorable,
    verdict, verdictBasis, governance,
    roleHrs, ctrRate: costs.ctrRate || 800,
  };
}

/** The per-role cost table shown above the summary. */
export function roleCostRows(S, deptId = 'ALL') {
  const loadedMult = S.costs?.loadedMult || 1.3;
  const roles = deptId === 'ALL' ? S.roles : S.roles.filter((r) => r.dept === deptId);

  return roles.map((role) => {
    // A Built budget is an authoritative loaded figure; a manual salary is not, so the two
    // are derived in opposite directions and the source is always shown.
    const plan = (S.headcountPlan ?? []).find((p) => p.roleId === role.id && p.annualBudget);
    const loadedCost = plan?.annualBudget ?? (role.salary || 0) * loadedMult;
    const baseSalary = plan ? plan.annualBudget / loadedMult : (role.salary || 0);
    const totalCashComp = baseSalary * (1 + (role.stiPct || 0) / 100);

    return {
      id: role.id,
      name: role.name,
      fromBuilt: !!plan,
      budgetCode: plan?.budgetCode ?? '',
      baseSalary: Math.round(baseSalary),
      stiPct: role.stiPct || 0,
      totalCashComp: Math.round(totalCashComp),
      loadedMult,
      loadedCost: Math.round(loadedCost),
      // 48 working weeks, not 52 — the legacy figure nets off holidays and leave rather
      // than pricing an hour against a year nobody actually works.
      costPerHour: Math.round((loadedCost / ((role.hrs || 40) * 48)) * 10) / 10,
    };
  });
}
